import React, { useEffect, useState } from 'react'
import { useHistory, useParams } from 'react-router'
import { analytics } from 'Modules/Analytics'
import { goals } from 'Modules/Analytics/trackActionInterface/Impact/Goals'
import { useQuery } from '@apollo/client'
import { GOALS } from 'Apollo/Queries/Goals'
import { useAppState } from 'Context/AppContext/AppContext'
import { Goal } from 'Generated/graphql-types'
import { GoalPageContainer } from './GoalPage.styled'
import { NewGoalCard } from '../GoalsDashboard/Goals/NewGoalCard'
import { SuperLoading } from '../../../SharedComponents/Loading/SuperLoading'
import { GoalPageEmptyState } from './EmptyStates/GoalPageEmptyState'
import { IMPACT_GOALS_PATH } from './utils'
import { StoryAiDialog } from '../ImpactStories/ImpactStoriesPage/StoryAiDialog/StoryAiDialog'
import { StoryVariants } from '../ImpactStories/ImpactStoriesPage/StoryAiDialog/StoryAiTypes'
import { BackToLink } from '../../../SharedComponents/BackToLink/BackToLink'

export const GoalPage = () => {
  const slug: { goalId: string } = useParams()
  const { goalId } = slug
  const history = useHistory()

  // Get section from URL if present
  const urlParams = new URLSearchParams(window.location.search)
  const sectionParam = urlParams.get('section')

  const {
    nonprofitOrgConfig: { orgId: nonprofitOrgId, role, platformPlan },
    donations: {
      campaigns: { data: campaignsData },
    },
  } = useAppState()

  const [shouldDefaultOpenDialog, setShouldDefaultOpenDialog] = useState(
    urlParams.has('dialogOpen')
  )

  const [preSelectedGoal, setPreSelectedGoal] = React.useState<
    Goal | undefined
  >(undefined)

  const { data: { goals: goalsData = [] } = {}, loading } = useQuery(GOALS, {
    variables: {
      activeOnly: true,
      goalId,
      nonprofitOrgId,
    },
    skip: !goalId || !nonprofitOrgId || goalId === 'wizard',
    notifyOnNetworkStatusChange: true,
    onCompleted: () => {
      performance.mark('goal page data loaded')
    },
  })

  const goalsWithLinkedCampaign = campaignsData.map(
    (item) => item.initiativeGoalId
  )

  const dispatchBackClickedEvent = () => {
    analytics.trackActionCreator(goals.Tab_Back_Clicked)({
      goalId,
      userAccountType: role,
      platformPlan,
    })
  }

  const goal: Goal = goalsData?.[0]

  if (goal && shouldDefaultOpenDialog) {
    setShouldDefaultOpenDialog(false)
    setPreSelectedGoal(goal)
  }

  const handleCloseDialog = () => {
    const queryParams = new URLSearchParams(window.location.search)
    if (queryParams.has('dialogOpen')) {
      queryParams.delete('dialogOpen')
      history.replace({
        search: queryParams.toString(),
      })
    }
  }

  useEffect(() => {
    if (!loading) {
      performance.mark('goal page render')
    }
  }, [loading])

  if (loading || !goalId || !nonprofitOrgId) return <SuperLoading />
  if (!goal) return <GoalPageEmptyState />

  return (
    <>
      <GoalPageContainer>
        <BackToLink
          dispatchBackClickedEvent={dispatchBackClickedEvent}
          label='Back to Goals'
          path={IMPACT_GOALS_PATH}
        />
        <NewGoalCard
          goal={goal}
          hasLinkedCampaign={goalsWithLinkedCampaign.includes(goal.id)}
          setPreSelectedGoal={setPreSelectedGoal}
          goalCardId={`goal-card-${goal.id}`}
        />
      </GoalPageContainer>
      <StoryAiDialog
        open={!!preSelectedGoal}
        handleClose={() => {
          setPreSelectedGoal(undefined)
          handleCloseDialog()
        }}
        storyEntryPoint={StoryVariants.GOALS}
        variantSpecificData={{
          goal: {
            goalArray: goalsData,
            preSelectedGoal: preSelectedGoal || undefined,
          },
        }}
      />
    </>
  )
}
